import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { audio } from "../audio/engine.js";
import { useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { UnitViewer } from "./UnitViewer.js";
import { unitName } from "./shared.js";
import { useReducedMotion } from "./useReducedMotion.js";
import type { UnitView } from "./types.js";
import "./star-ceremony.css";

/** A server-confirmed upgrade; dismissing the presentation never mutates the unit. */
export function StarCeremony({ unit, onClose }: { unit: UnitView; onClose: () => void }) {
  const t = useT();
  const reducedMotion = useReducedMotion();
  const dialog = useRef<HTMLDialogElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const viewer = useRef<UnitViewer | null>(null);
  const motion = useRef(reducedMotion);
  motion.current = reducedMotion;
  const [phase, setPhase] = useState<"loading" | "playing" | "settled" | "error">("loading");

  useEffect(() => {
    const element = dialog.current!;
    const previous = document.activeElement;
    element.showModal();
    return () => {
      element.close();
      if (previous instanceof HTMLElement && previous.isConnected && !previous.matches(":disabled"))
        previous.focus();
      else
        document
          .querySelector<HTMLButtonElement>("[data-testid=unit-detail] [data-testid=sheet-close]")
          ?.focus();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let current: UnitViewer | null = null;
    let observer: ResizeObserver | null = null;
    try {
      current = new UnitViewer(canvas.current!, { accent: "#e0ac45", reducedMotion: motion.current });
      viewer.current = current;
      current.setSpin(0);
      current.setSkin(unit.skin);
      observer = new ResizeObserver(() => current?.resize());
      observer.observe(canvas.current!);
      const scene = current;
      void scene
        .show(unit.unitId)
        .then(() => {
          if (cancelled) return;
          setPhase("playing");
          audio.play("reward");
          scene.celebrateStars(unit.stars, () => {
            if (!cancelled) setPhase("settled");
          });
        })
        .catch((error: unknown) => {
          if (!cancelled) {
            console.error("Unable to render star ceremony", error);
            setPhase("error");
          }
        });
    } catch (error) {
      console.error("Unable to initialize star ceremony", error);
      setPhase("error");
    }
    return () => {
      cancelled = true;
      observer?.disconnect();
      current?.dispose();
      viewer.current = null;
    };
  }, [unit.unitId, unit.stars, unit.skin]);

  useEffect(() => {
    viewer.current?.setReducedMotion(reducedMotion);
  }, [reducedMotion]);

  return createPortal(
    <dialog
      ref={dialog}
      className={`star-ceremony${reducedMotion ? " star-ceremony-reduced" : ""}`}
      aria-labelledby="star-ceremony-title"
      aria-describedby="star-ceremony-result"
      data-testid="star-ceremony"
      data-phase={phase}
      data-stars={unit.stars}
      data-unit-id={unit.unitId}
      data-reduced-motion={String(reducedMotion)}
      onCancel={(event) => {
        // React bubbles `cancel` through the portal to the unit sheet's dialog; stop it there so Escape closes only the ceremony.
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }}
    >
      <header className="star-ceremony-heading">
        <p className="star-ceremony-eyebrow">{t("units.ceremony.title")}</p>
        <h2 id="star-ceremony-title">{unitName(unit.unitId)}</h2>
        <p id="star-ceremony-result" role="status">
          {t("units.ceremony.stars", { n: unit.stars })}
        </p>
        <button
          type="button"
          className="star-ceremony-dismiss"
          aria-label={t("common.close")}
          onClick={onClose}
          data-testid="star-ceremony-close"
        >
          ×
        </button>
      </header>
      <div
        className="star-ceremony-stage"
        aria-label={t("units.ceremony.model", { name: unitName(unit.unitId) })}
      >
        <canvas ref={canvas} data-testid="star-ceremony-canvas" aria-hidden="true" />
        {phase === "loading" ? (
          <p className="star-ceremony-message" role="status">
            {t("common.loading")}
          </p>
        ) : null}
        {phase === "error" ? (
          <p className="star-ceremony-message" role="alert">
            {t("units.ceremony.modelError")}
          </p>
        ) : null}
      </div>
      <footer className="star-ceremony-footer">
        <div className="star-ceremony-stats">
          <span>
            {t("units.ceremony.power")} <strong>{formatCompact(unit.power)}</strong>
          </span>
          <span>
            {t("units.ceremony.levelCap")} <strong>{unit.levelCap}</strong>
          </span>
        </div>
        <p>{t(unit.stars === 5 ? "units.ceremony.awakened" : "units.ceremony.saved")}</p>
        {phase === "playing" ? (
          <Button
            variant="gold"
            onClick={() => viewer.current?.finishCeremony()}
            data-testid="star-ceremony-skip"
          >
            {t("units.ceremony.skip")}
          </Button>
        ) : (
          <Button variant="gold" onClick={onClose} data-testid="star-ceremony-continue">
            {t("units.ceremony.continue")}
          </Button>
        )}
      </footer>
    </dialog>,
    document.body,
  );
}
